import { useState, useCallback, useEffect } from "react";
import { RefreshCcw, Sparkles, Trophy, Target } from "lucide-react";
import { Button } from "@/components/ui/button";

const BALL_COLORS = ["#C65D47", "#8C9D78", "#1D3557", "#FFD700", "#4FC3F7", "#E91E63"];

const LotteryDraw = () => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [playerNumbers, setPlayerNumbers] = useState([]);
  const [drawnNumbers, setDrawnNumbers] = useState([]);
  const [currentBall, setCurrentBall] = useState(null);
  const [matchCount, setMatchCount] = useState(0);
  const [gamePhase, setGamePhase] = useState("pick"); // pick, drawing, result
  const [availableNumbers] = useState(() => Array.from({ length: 30 }, (_, i) => i + 1));

  const toggleNumber = (num) => {
    if (gamePhase !== "pick") return;
    if (playerNumbers.includes(num)) {
      setPlayerNumbers(playerNumbers.filter((n) => n !== num));
    } else if (playerNumbers.length < 6) {
      setPlayerNumbers([...playerNumbers, num]);
    }
  };

  const quickPick = () => {
    if (gamePhase !== "pick") return;
    const shuffled = [...availableNumbers].sort(() => Math.random() - 0.5);
    setPlayerNumbers(shuffled.slice(0, 6).sort((a, b) => a - b));
  };

  const startDraw = useCallback(() => {
    if (playerNumbers.length !== 6) return;
    setGamePhase("drawing");
    setIsDrawing(true);
    setDrawnNumbers([]);
    setMatchCount(0);

    // Generate 6 random winning numbers
    const winning = [];
    const pool = [...availableNumbers];
    for (let i = 0; i < 6; i++) {
      const idx = Math.floor(Math.random() * pool.length);
      winning.push(pool[idx]);
      pool.splice(idx, 1);
    }

    // Animate drawing one by one
    let drawn = [];
    let matches = 0;
    winning.forEach((num, i) => {
      setTimeout(() => {
        setCurrentBall(num);
        setTimeout(() => {
          drawn = [...drawn, num];
          setDrawnNumbers([...drawn]);
          setCurrentBall(null);
          if (playerNumbers.includes(num)) {
            matches++;
            setMatchCount(matches);
          }
          if (i === 5) {
            setTimeout(() => {
              setIsDrawing(false);
              setGamePhase("result");
            }, 600);
          }
        }, 800);
      }, i * 1600);
    });
  }, [playerNumbers, availableNumbers]);

  const reset = () => {
    setPlayerNumbers([]);
    setDrawnNumbers([]);
    setCurrentBall(null);
    setMatchCount(0);
    setGamePhase("pick");
    setIsDrawing(false);
  };

  const getPrize = () => {
    if (matchCount >= 6) return { text: "JACKPOT !", desc: "Tous les numéros !", tier: "jackpot" };
    if (matchCount >= 5) return { text: "SUPER GAIN", desc: "5 numéros sur 6 !", tier: "gold" };
    if (matchCount >= 4) return { text: "BEAU GAIN", desc: "4 numéros sur 6 !", tier: "silver" };
    if (matchCount >= 3) return { text: "PETIT GAIN", desc: "3 numéros sur 6 !", tier: "bronze" };
    if (matchCount >= 2) return { text: "REMBOURSÉ", desc: "2 numéros sur 6", tier: "min" };
    return { text: "PERDU", desc: `${matchCount} numéro${matchCount > 1 ? "s" : ""} trouvé${matchCount > 1 ? "s" : ""}`, tier: "lost" };
  };

  return (
    <div className="text-center" data-testid="lottery-draw">
      {/* Phase: Pick numbers */}
      {gamePhase === "pick" && (
        <div className="space-y-5">
          <div className="flex items-center justify-center gap-4 mb-2">
            <Target className="w-5 h-5 text-[#1D3557]" />
            <p className="text-[#1D3557] font-semibold">
              Choisissez 6 numéros ({playerNumbers.length}/6)
            </p>
          </div>

          {/* Number grid */}
          <div className="grid grid-cols-6 gap-2 max-w-sm mx-auto">
            {availableNumbers.map((num) => {
              const selected = playerNumbers.includes(num);
              return (
                <button
                  key={num}
                  onClick={() => toggleNumber(num)}
                  className={`w-10 h-10 rounded-full font-bold text-sm transition-all duration-200 ${
                    selected
                      ? "bg-[#C65D47] text-white scale-110 shadow-lg shadow-[#C65D47]/30"
                      : "bg-stone-100 text-stone-600 hover:bg-[#8C9D78]/20 hover:text-[#1D3557]"
                  }`}
                  data-testid={`lottery-num-${num}`}
                >
                  {num}
                </button>
              );
            })}
          </div>

          {/* Selected numbers display */}
          <div className="flex items-center justify-center gap-2 min-h-[48px]">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm border-2 transition-all ${
                  playerNumbers[i]
                    ? "bg-[#1D3557] text-white border-[#1D3557]"
                    : "bg-transparent border-dashed border-stone-300 text-stone-300"
                }`}
              >
                {playerNumbers[i] || "?"}
              </div>
            ))}
          </div>

          <div className="flex gap-3 justify-center">
            <Button
              onClick={quickPick}
              variant="outline"
              className="rounded-full px-6 border-[#8C9D78]/30 hover:bg-[#8C9D78]/10"
              data-testid="lottery-quick-pick"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Tirage rapide
            </Button>
            <Button
              onClick={startDraw}
              disabled={playerNumbers.length !== 6}
              className="bg-[#C65D47] hover:bg-[#A84A38] rounded-full px-8 shadow-lg disabled:opacity-50"
              data-testid="lottery-start-btn"
            >
              <Trophy className="w-4 h-4 mr-2" />
              Lancer le tirage
            </Button>
          </div>
        </div>
      )}

      {/* Phase: Drawing */}
      {(gamePhase === "drawing" || gamePhase === "result") && (
        <div className="space-y-6">
          {/* Player numbers */}
          <div>
            <p className="text-xs text-stone-400 mb-2 uppercase tracking-wider">Vos numéros</p>
            <div className="flex items-center justify-center gap-2">
              {playerNumbers.sort((a, b) => a - b).map((num) => {
                const matched = drawnNumbers.includes(num);
                return (
                  <div
                    key={num}
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-500 ${
                      matched
                        ? "bg-[#8C9D78] text-white scale-110 shadow-lg shadow-[#8C9D78]/30 ring-2 ring-[#8C9D78]/50"
                        : "bg-[#1D3557] text-white"
                    }`}
                  >
                    {num}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Current ball animation */}
          {currentBall && (
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FFA000] flex items-center justify-center shadow-2xl animate-bounce">
                <span className="text-[#1D3557] font-bold text-xl">{currentBall}</span>
              </div>
            </div>
          )}

          {/* Drawn numbers */}
          <div>
            <p className="text-xs text-stone-400 mb-2 uppercase tracking-wider">Numéros tirés</p>
            <div className="flex items-center justify-center gap-2 min-h-[48px]">
              {Array.from({ length: 6 }).map((_, i) => {
                const num = drawnNumbers[i];
                const isMatch = num && playerNumbers.includes(num);
                return (
                  <div
                    key={i}
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-500 ${
                      num
                        ? isMatch
                          ? "bg-[#8C9D78] text-white ring-2 ring-[#8C9D78]/50"
                          : "bg-[#C65D47] text-white"
                        : "bg-stone-200 border-2 border-dashed border-stone-300"
                    }`}
                    style={{
                      animationDelay: `${i * 0.1}s`,
                    }}
                  >
                    {num || ""}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Match counter */}
          <div className="flex items-center justify-center gap-2">
            <Trophy className="w-5 h-5 text-[#FFD700]" />
            <span className="font-bold text-[#1D3557]">{matchCount} correspondance{matchCount > 1 ? "s" : ""}</span>
          </div>

          {/* Result */}
          {gamePhase === "result" && (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
              {(() => {
                const prize = getPrize();
                return (
                  <div
                    className={`p-5 rounded-2xl border-2 ${
                      prize.tier === "lost"
                        ? "bg-stone-50 border-stone-200"
                        : prize.tier === "jackpot"
                        ? "bg-gradient-to-r from-[#FFD700]/20 to-[#C65D47]/20 border-[#FFD700]/50"
                        : "bg-gradient-to-r from-[#8C9D78]/10 to-[#C65D47]/10 border-[#8C9D78]/30"
                    }`}
                    data-testid="lottery-result"
                  >
                    <p className={`font-bold text-xl ${prize.tier === "lost" ? "text-stone-500" : "text-[#1D3557]"}`}>
                      {prize.text}
                    </p>
                    <p className="text-stone-500 text-sm mt-1">{prize.desc}</p>
                  </div>
                );
              })()}

              <Button
                onClick={reset}
                className="bg-[#C65D47] hover:bg-[#A84A38] rounded-full px-8 shadow-lg"
                data-testid="lottery-reset-btn"
              >
                <RefreshCcw className="w-4 h-4 mr-2" />
                Nouveau tirage
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LotteryDraw;
